/**
 * CypAdhdPadBridge — presents a CYP ADHD patient's modular data as the legacy
 * PatientDataContext, so the ADULT PAD pages run UNCHANGED for children.
 *
 * The CYP PAD pages are literal copies of the adult ones (Eve, 28 Aug: the
 * front end is duplicated, never re-imagined). Those pages read and write
 * `patientDataModel.dataModel['5_postAssessmentDiscussion']` through
 * `updatePatientFirestore`. CYP has no monolithic dataModel — its PAD data
 * lives at assessmentStages/postAssessmentDiscussion — so this provider:
 *
 *   READS  — synthesises a patientDataModel from the modular header:
 *     dataModel['5_postAssessmentDiscussion']  <- stage.data.legacyModel
 *       (seeded from CYP_PAD_SECTION_MODEL on first open)
 *     dataModel['4_diagnosticDecision']        <- the CYP DD stage's decision,
 *       exposed flat AND under a titled section so both ShouldDisplayElement
 *       lookup styles and isPadAdhdOutcomePositive resolve
 *     dataModel['6_manageReports']             <- the PAD reportContent doc's
 *       pad* flags (padReportGenerated / Approved / timestamps)
 *     formData.yourHealth                      <- the GUARDIAN's medical
 *       history physical-health answers, in the adult yourHealth shape the
 *       HLT page's prefill scan expects ("Your Physical Health" subsection)
 *     formData.yourInformation                 <- the child's sex assigned
 *       at birth from the patient header, in the adult shape the Next Steps
 *       "Females with ADHD" hint reads (getPatientSexAtBirth)
 *
 *   WRITES — translates updatePatientFirestore(patientId, updates):
 *     '5_postAssessmentDiscussion' -> deep-merged into stage data.legacyModel
 *     '6_manageReports' pad* flags -> the PAD reportContent doc, plus stage
 *       status 'completed' when padReportGenerated lands (so the overview
 *       pill and documents dashboard see the finish)
 */

import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { doc as firestoreDoc, updateDoc, setDoc } from 'firebase/firestore';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { db } from '../../../firebase';
import { getCypAdhdReportContentPathSegments } from '../../../constants/cypAdhdFirestorePaths';
import { fetchEnrichedInformantData } from '../../../utils/informantFirestorePaths';
import { deepMerge } from '../../../utils/deepMerge';
import { useAuth } from '../../../hooks/useAuth';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';
import { getCypPatientSexAtBirth, CYP_SEX_AT_BIRTH_OPTIONS } from '../../../utils/conditionChecks';

import {
  CYP_PAD_SECTION_MODEL,
  cypPadLetterContentIsCurrent,
  reconcileCypPadLegacyModel,
} from './cypAdhdPadDataModel';

const STAGE_KEY = 'postAssessmentDiscussion';

/**
 * The Health Checks readings tree, in the adult CI section-8 shape the copied
 * 4_HLT page reads and writes (assessment_data_model.py sections['8']). CYP
 * has no monolithic clinical interview, so the readings the clinician takes
 * during the PAD live on the PAD stage doc (data.healthChecksLegacy) and are
 * presented to the page as dataModel['2_clinicalInterview'].sections['8'].
 * prefilledByServiceUserVarNames matches the GUARDIAN's medical-history
 * variables (their_medical_history.py subsection 7), which the bridge's
 * formData.yourHealth carries — so the prefill source dialogs resolve.
 */
/**
 * The Health Checks readings tree for CYP ADHD PAD. Contains only height,
 * weight, and BMI — pulse and blood pressure are not collected during the
 * CYP PAD (they will be obtained outside the platform). The 4_HLT.js
 * rendering also filters by title as a safety net for any legacy data that
 * was stored before this change. The adult PAD retains pulse and BP.
 */
export const CYP_PAD_HEALTH_CHECKS_MODEL = {
  sections: {
    8: {
      sectionTitle: 'Physical Health',
      subsections: {
        1: {
          subsectionTitle: 'Physical Health',
          subsectionVarName: 'physicalHealth',
          subsectionQuestions: {
            1: {
              title: 'Height',
              info: '',
              patientPrefill: null,
              clinicianInput: null,
              providerVarName: 'height',
              prefilledByServiceUserVarNames: ['heightCm', 'heightFtIn'],
            },
            2: {
              title: 'Weight',
              info: '',
              patientPrefill: null,
              clinicianInput: null,
              providerVarName: 'weight',
              prefilledByServiceUserVarNames: ['weightKg', 'weightLb'],
            },
            3: {
              title: 'BMI',
              info: '',
              patientPrefill: null,
              clinicianInput: null,
              providerVarName: 'bmi',
              prefilledByServiceUserVarNames: ['weightKg', 'weightLb', 'heightCm', 'heightFtIn'],
            },
          },
        },
      },
    },
  },
};

/**
 * Deep-clone a legacy tree, copying only plain objects/arrays. Firestore
 * Timestamps and FieldValue sentinels (serverTimestamp()) are kept by
 * reference — JSON round-tripping them turns stored timestamps into
 * {seconds, nanoseconds} maps and sentinels into junk that would then be
 * written back over real values on the next chained merge.
 */
function cloneTree(value) {
  if (Array.isArray(value)) return value.map(cloneTree);
  if (value && typeof value === 'object' && value.constructor === Object) {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, cloneTree(v)]),
    );
  }
  return value;
}

/**
 * An informant form instance stores answers in one of two shapes — flat
 * (`answers[varName]`, the live form runner) or nested under the question key
 * (`answers[qKey][varName]`, the demo/QA seeders) — so a bare `answers[var]`
 * lookup silently loses every seeded record. Same resolution order as
 * scaleScoringAdapter.resolveAnswer.
 */
function resolveGuardianAnswer(answers, varName) {
  if (!answers || typeof answers !== 'object') return null;
  if (answers[varName] !== undefined) return answers[varName];
  for (const value of Object.values(answers)) {
    if (value && typeof value === 'object' && !Array.isArray(value)
      && value[varName] !== undefined) {
      return value[varName];
    }
  }
  return null;
}

/**
 * The guardian's physical-health answers in the adult yourHealth shape.
 * CYP ADHD only collects height and weight from parents — pulse and blood
 * pressure questions were removed from the parent/carer questionnaire.
 */
function buildYourHealthFromGuardian(answers) {
  const questionFor = (title, subs) => ({
    questionTitle: title,
    subquestions: Object.fromEntries(subs.map(([key, varName, text, inputType], index) => [
      String(index + 1),
      {
        serviceUserVarName: varName,
        subquestionDisplayText: text,
        inputType,
        answer: resolveGuardianAnswer(answers, varName),
      },
    ])),
  });
  return {
    formSubsections: {
      // The HLT prefill scan matches by subsection TITLE, so the adult title
      // is used even though the answers come from the parent's form.
      7: {
        formSubsectionTitle: 'Your Physical Health',
        questions: {
          1: questionFor('Weight', [
            ['1', 'weightKg', 'Weight in kilograms (kg)', 'freeTextNumerical'],
            ['2', 'weightLb', 'Weight in pounds (lb)', 'freeTextNumerical'],
          ]),
          2: questionFor('Height', [
            ['1', 'heightCm', 'Height in centimetres (cm)', 'freeTextNumerical'],
            ['2', 'heightFtIn', 'Height in feet and inches (ft/in)', 'freeText'],
          ]),
        },
      },
    },
  };
}

/**
 * The child's sex assigned at birth in the adult yourInformation shape.
 *
 * The copied Next Steps page (6_NS) shows "Patient's sex assigned at birth"
 * under the "Females with ADHD" resource decision, and opens a source dialog
 * for it, by reading formData.yourInformation through the adult helpers
 * (getPatientSexAtBirth). An adult answers that question on their own form;
 * for a child the value lives on the patient header, written at setup and
 * overwritten by the parent's confirmed answer on About you and consent.
 * This builds the one-question form the adult helpers expect from that
 * header value, so the page's hint and dialog work unchanged for children.
 * No value recorded means answer null: the hint is then not shown.
 *
 * @param {Object|null|undefined} header - The patient header (ModularPatientDataContext.header)
 * @returns {Object} A yourInformation form in the adult formData shape
 */
function buildYourInformationFromHeader(header) {
  const value = getCypPatientSexAtBirth(header);
  return {
    formSubsections: {
      1: {
        formSubsectionTitle: 'About the child',
        questions: {
          1: {
            questionTitle: 'Sex assigned at birth',
            questionVarName: 'sexAssignedAtBirth',
            subquestions: {
              1: {
                serviceUserVarName: 'sexAssignedAtBirth',
                subquestionDisplayText: "At birth, what was the child's recorded sex?",
                inputType: 'selectOne',
                options: CYP_SEX_AT_BIRTH_OPTIONS,
                answer: value ? { value } : null,
              },
            },
          },
        },
      },
    },
  };
}

/**
 * Find a CI stage question node by provider variable, scanning every section
 * (the CI tree is authored server-side; indices are layout, not identity).
 */
function findCiNodeByProviderVar(ciSections, varName) {
  for (const section of Object.values(ciSections || {})) {
    for (const subsection of Object.values(section?.subsections || {})) {
      for (const question of Object.values(subsection?.subsectionQuestions || {})) {
        const vars = Array.isArray(question?.providerVarNames)
          ? question.providerVarNames
          : (question?.providerVarName ? [question.providerVarName] : []);
        if (vars.includes(varName)) return question;
      }
    }
  }
  return null;
}

export default function CypAdhdPadBridge({ children }) {
  const { patientId } = useParams();
  const { claims, user } = useAuth();
  const {
    header,
    assessmentStages,
    reportContent,
    updateAssessmentStage,
    flushStageWrites,
    fetchPatientData,
    error,
  } = useContext(ModularPatientDataContext);

  const padStage = assessmentStages?.[STAGE_KEY];
  const ddStage = assessmentStages?.diagnosticDecision;
  const ciStage = assessmentStages?.clinicalInterview;

  // Persist the current PAD question tree once, for the clinician who can
  // edit the stage.
  //
  // Two cases share this write:
  //   - the stage has never been opened, so there is no legacyModel yet
  //     (the original seed);
  //   - the stage was opened while Strategies was empty and Treatment Options
  //     still said "Option 1" / "Option 2". Those patients would otherwise
  //     keep the old tree forever, because the seed only runs when the field
  //     is missing. Reconciling copies any letter answer that still matches
  //     a field name (the medications) and leaves treatment preferences and
  //     the health-checks answer where they are.
  //
  // A previewer, a non-assigned clinician, or Support must not write.
  // Support has no permission on assessmentStages, and the unguarded seed
  // used to surface a "changes may not have saved" toast. They still see the
  // reconciled tree in memory, from patientDataModel below.
  const isPreviewMode = useIsPreviewMode();
  const canEdit = useCanEditStage(header, 'PAD', { previewMode: isPreviewMode });
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (!canEdit) return;
    if (!padStage) return;
    const stored = padStage?.data?.legacyModel;
    if (stored && cypPadLetterContentIsCurrent(stored)) return;
    seededRef.current = true;
    // A click that landed before this effect already merged onto the
    // reconciled tree and stored that tree on the ref. Writing the ref back
    // keeps that answer. Otherwise write the reconciled snapshot of what is
    // stored. The whole legacyModel is one Firestore field, so a later write
    // of a stale snapshot would wipe the click.
    const next = legacyModelRef.current && cypPadLetterContentIsCurrent(legacyModelRef.current)
      ? legacyModelRef.current
      : reconcileCypPadLegacyModel(stored);
    legacyModelRef.current = next;
    updateAssessmentStage(STAGE_KEY, { 'data.legacyModel': next });
  }, [canEdit, padStage, updateAssessmentStage]);

  // The guardian's medical-history answers for the HLT readings prefill.
  const informantsMap = header?.informants || {};
  const [guardianAnswers, setGuardianAnswers] = useState(null);
  useEffect(() => {
    let isCurrent = true;
    const subcollectionAccess = {
      organisation: header?.organisation || claims?.organisation || '',
      clinicianUid: user?.uid || '',
      isOrgWideAccess: claims?.userType === 'Org. Admin' || claims?.userType === 'Support',
    };
    const load = async () => {
      for (const [informantId, mapEntry] of Object.entries(informantsMap)) {
        if (mapEntry?.informantRole !== 'legalGuardian') continue;
        try {
          const enriched = await fetchEnrichedInformantData(
            informantId, patientId, mapEntry, subcollectionAccess,
          );
          if (!isCurrent) return;
          setGuardianAnswers(enriched?.formInstances?.theirMedicalHistory?.answers || {});
          return;
        } catch (err) {
          console.warn(`PAD bridge: could not load guardian ${informantId}:`, err);
        }
      }
      if (isCurrent) setGuardianAnswers({});
    };
    if (Object.keys(informantsMap).length > 0) load();
    else setGuardianAnswers({});
    return () => { isCurrent = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, header?.informants]);

  const patientDataModel = useMemo(() => {
    if (!header) return null;
    const diagnosisDecision = ddStage?.data?.decision?.diagnosisDecision ?? null;
    const padReport = reportContent?.[STAGE_KEY] || {};
    // Always the current question list, even when the stored tree is the old
    // empty Strategies / "Option 1" shape. Saved answers are copied across.
    // Read-only viewers get this in memory; the effect above persists it
    // only for the assigned clinician.
    const legacyModel = reconcileCypPadLegacyModel(padStage?.data?.legacyModel);
    // Clone before overlaying: the stored tree is context state and the
    // display-only prefills below must never mutate it.
    const healthChecks = cloneTree(
      padStage?.data?.healthChecksLegacy || CYP_PAD_HEALTH_CHECKS_MODEL,
    );

    // Overlay the PARENT'S answers onto the readings tree, display-only. The
    // CI stage already carries the guardian's medical-history measurements as
    // formatted prefill strings ("34 kg", "120/80 mmHg" — stamped server-side
    // by cyp_adhd_ci_prefill); without this the HLT page told clinicians
    // nothing was recorded even though the parent had answered (HK testing,
    // 2 Sept). Never overwrites a stored value.
    const ciSections = ciStage?.data?.sections;
    const readingNodes = healthChecks?.sections?.['8']?.subsections?.['1']?.subsectionQuestions
      || healthChecks?.sections?.[8]?.subsections?.[1]?.subsectionQuestions || {};
    Object.values(readingNodes).forEach((node) => {
      if (!node || node.patientPrefill) return;
      const ciNode = findCiNodeByProviderVar(ciSections, node.providerVarName);
      if (ciNode?.guardianPrefill) {
        node.patientPrefill = ciNode.guardianPrefill;
        node.patientPrefillLastUpdatedAt = ciNode.guardianPrefillLastUpdatedAt || null;
      }
    });

    // BMI from the parent's metric measurements, display-only (the BMI row
    // otherwise sits empty until a clinician edits height or weight — HK
    // testing, 4 Sept). Mechanical arithmetic only: the number, never an
    // adult BMI category, which does not apply to children.
    const nodesByVar = {};
    Object.values(readingNodes).forEach((node) => {
      if (node?.providerVarName) nodesByVar[node.providerVarName] = node;
    });
    const bmiNode = nodesByVar.bmi;
    if (bmiNode && !bmiNode.patientPrefill && !bmiNode.clinicianInput) {
      const metric = (value, unit) => {
        const match = String(value ?? '').match(
          new RegExp(`(\\d+(?:\\.\\d+)?)\\s*${unit}`),
        );
        return match ? parseFloat(match[1]) : null;
      };
      const heightCm = metric(
        nodesByVar.height?.clinicianInput || nodesByVar.height?.patientPrefill, 'cm',
      );
      const weightKg = metric(
        nodesByVar.weight?.clinicianInput || nodesByVar.weight?.patientPrefill, 'kg',
      );
      if (heightCm && weightKg) {
        const bmi = weightKg / ((heightCm / 100) ** 2);
        bmiNode.patientPrefill = String(Math.round(bmi * 10) / 10);
      }
    }

    // The parent's hoped outcomes, presented as the adult 'assessmentGoals'
    // CI node (canonical path: section 2 / subsection 1 / question 4) so
    // mdtPlanUtils.resolveAssessmentGoalsDisplay finds them and the PAD
    // Review's read-only Assessment goals row stops reading empty.
    const hopedOutcomesNode = findCiNodeByProviderVar(ciSections, 'hopedOutcomes');
    if (hopedOutcomesNode?.guardianPrefill) {
      healthChecks.sections = {
        ...healthChecks.sections,
        2: {
          sectionTitle: 'Presenting Complaint',
          subsections: {
            1: {
              subsectionQuestions: {
                4: {
                  title: 'Assessment goals',
                  providerVarName: 'assessmentGoals',
                  patientPrefill: hopedOutcomesNode.guardianPrefill,
                  patientPrefillLastUpdatedAt:
                    hopedOutcomesNode.guardianPrefillLastUpdatedAt || null,
                  clinicianInput: null,
                  prefilledByServiceUserVarNames: ['hopedOutcomes'],
                },
              },
            },
          },
        },
      };
    }
    return {
      ...header,
      dataModel: {
        '5_postAssessmentDiscussion': legacyModel,
        // The Health Checks readings, in the adult CI shape 4_HLT reads.
        '2_clinicalInterview': {
          ...healthChecks,
          // Treatment contraindication screening (WLC): the Yes/No decisions
          // recorded on the CI's Medical and Family History steps, passed
          // through read-only so 4_HLT can show them under the readings.
          // Never written back: updatePatientFirestore merges only the
          // section-8 reading updates 4_HLT sends, so this field cannot
          // leak into healthChecksLegacy.
          medicalScreening: ciStage?.medicalScreening || null,
        },
        // Both lookup styles: flat on the module AND under a titled section
        // (ShouldDisplayElement scans sections by sectionTitle; other helpers
        // read the module directly). sectionVarName is load-bearing:
        // isADHDDiagnosisPositive locates the section by
        // sectionVarName === 'diagnosticDecision', and it gates the forward
        // navigation to Treatment Options and the TRT/HLT sidebar unlocks.
        '4_diagnosticDecision': {
          diagnosisDecision,
          mdtPlan: {},
          lastCompletedAt: ddStage?.lastCompletedAt || ddStage?.completedAt || null,
          sections: {
            1: {
              sectionTitle: 'Diagnostic Decision',
              sectionVarName: 'diagnosticDecision',
              diagnosisDecision,
            },
          },
        },
        '6_manageReports': {
          padReportGenerated: padReport.padReportGenerated,
          padReportGeneratedAt: padReport.padReportGeneratedAt,
          padReportFirstGeneratedAt: padReport.padReportFirstGeneratedAt,
          padReportApproved: padReport.padReportApproved,
        },
      },
      formData: {
        yourHealth: buildYourHealthFromGuardian(guardianAnswers),
        // The child's sex assigned at birth (from the header), so the Next
        // Steps "Females with ADHD" decision shows the same hint and source
        // dialog it shows for an adult.
        yourInformation: buildYourInformationFromHeader(header),
      },
    };
  }, [header, padStage?.data?.legacyModel, padStage?.data?.healthChecksLegacy,
    ciStage?.data?.sections, ciStage?.medicalScreening,
    ddStage?.data?.decision?.diagnosisDecision, ddStage?.lastCompletedAt,
    ddStage?.completedAt, reportContent, guardianAnswers]);

  // Base trees for write merges. Successive writes must chain on the LAST
  // MERGED tree, not on context state: the stage write is debounced and
  // last-write-wins per dot-path key, so two quick writes computed from the
  // same stale context base would silently drop the first (e.g. flushing
  // unblurred notes and then immediately stamping the section completed).
  const legacyModelRef = useRef(null);
  const healthChecksRef = useRef(null);

  // The legacy write API the copied pages call. Nested-object updates are
  // deep-merged, exactly as the legacy context deep-merges dataModel writes.
  const updatePatientFirestore = useCallback(async (pid, updates) => {
    const padUpdate = updates?.['5_postAssessmentDiscussion'];
    if (padUpdate) {
      // Merge onto the reconciled tree, not the raw stored one. Otherwise the
      // first click on a patient who still has the old "Option 1" tree would
      // save that click against the old questions and drop the new prompts.
      const current = legacyModelRef.current
        || reconcileCypPadLegacyModel(padStage?.data?.legacyModel)
        || CYP_PAD_SECTION_MODEL;
      const merged = deepMerge(cloneTree(current), padUpdate);
      legacyModelRef.current = merged;
      updateAssessmentStage(STAGE_KEY, {
        'data.legacyModel': merged,
        ...(padStage?.status === 'notStarted' || !padStage?.status
          ? { status: 'inProgress' } : {}),
      });
    }

    // Health Checks readings (the copied 4_HLT writes them as adult CI
    // section-8 updates) — merged into the PAD stage doc's own tree.
    const ciUpdate = updates?.['2_clinicalInterview'];
    if (ciUpdate) {
      const current = healthChecksRef.current
        || padStage?.data?.healthChecksLegacy
        || CYP_PAD_HEALTH_CHECKS_MODEL;
      const merged = deepMerge(cloneTree(current), ciUpdate);
      healthChecksRef.current = merged;
      updateAssessmentStage(STAGE_KEY, { 'data.healthChecksLegacy': merged });
    }
    const manageReports = updates?.['6_manageReports'];
    if (manageReports) {
      // pad* flags land on the PAD reportContent doc — the same doc the CYP
      // review pages and documents dashboard read.
      const ref = firestoreDoc(db, ...getCypAdhdReportContentPathSegments(pid, STAGE_KEY));
      try {
        await updateDoc(ref, manageReports);
      } catch (e) {
        // First write can precede the doc (created at patient setup for new
        // patients, absent for older ones) — create-and-merge instead.
        await setDoc(ref, manageReports, { merge: true });
      }
      if (manageReports.padReportGenerated === true) {
        updateAssessmentStage(STAGE_KEY, { status: 'completed', progress: 1 });
        // The PAD now has an LLM pipeline for Review of Assessment: completion
        // triggers draft generation (via TriggerGenerateDocumentSection in
        // 1_REV.js), and the clinician reviews/approves on the PAD review
        // page (RD_PAD_CypAdhd). The review page exit renders the document —
        // NOT this bridge. Triggering here would render before the clinician
        // has reviewed, producing a document with unapproved AI text.
      }
    }
  }, [padStage, updateAssessmentStage, user?.uid]);

  const bridgeValue = useMemo(() => ({
    patientDataModel,
    updatePatientFirestore,
    flushPendingUpdates: flushStageWrites,
    fetchPatientData,
    error,
  }), [patientDataModel, updatePatientFirestore, flushStageWrites, fetchPatientData, error]);

  return (
    <PatientDataContext.Provider value={bridgeValue}>
      {children}
    </PatientDataContext.Provider>
  );
}
